import React, { useRef, useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Hand, Drumstick, Gamepad2, Moon, Pill, Sparkles, Move3d } from "lucide-react";
import MochiPet3D from "../components/mochi/MochiPet3D";
import { ACCESSORIES } from "../data/mochi";
import { HOLIDAY_ACC } from "../data/holidays";

const ALL_ACC = [...ACCESSORIES.map((a) => ({ id: a.id, label: a.label })), ...Object.entries(HOLIDAY_ACC).map(([id, m]) => ({ id, label: m.label }))];

// Rig-spec moods + Indonesian labels
const MOODS = [
  { id: "happy", label: "Happy", note: "bahagia" },
  { id: "excited", label: "Excited", note: "senang" },
  { id: "hungry", label: "Hungry", note: "lapar" },
  { id: "sleepy", label: "Sleepy", note: "mengantuk" },
  { id: "tired", label: "Tired", note: "lelah" },
  { id: "sick", label: "Sick", note: "sakit" },
  { id: "sad", label: "Sad", note: "sedih" },
  { id: "dead", label: "Dead", note: "pingsan" },
  { id: "revive", label: "Revive", note: "bangkit" },
];

const ACTIONS = [
  { id: "pet", label: "Elus", icon: Hand },
  { id: "feed", label: "Beri makan", icon: Drumstick },
  { id: "play", label: "Bermain", icon: Gamepad2 },
  { id: "sleep", label: "Tidur", icon: Moon },
  { id: "heal", label: "Sembuhkan", icon: Pill },
  { id: "revive", label: "Bangkitkan", icon: Sparkles },
];

export default function MochiDemo() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [active, setActive] = useState("happy");
  const [look, setLook] = useState(0);
  const [acc, setAcc] = useState("bow");

  const setMood = (m) => { setActive(m); ref.current?.setMood(m); };
  const doAction = (a) => { ref.current?.[a]?.(); };
  const onLook = (v) => { setLook(v); ref.current?.setLookOffset(v, v * 0.4); };

  return (
    <div className="kk-reveal">
      <button onClick={() => navigate("/mochi")} data-testid="demo-back"
        className="mb-4 flex items-center gap-1 text-[14px] font-semibold text-[#7c7277] hover:text-[#1e1b18]">
        <ChevronLeft className="h-4 w-4" /> Kembali ke Mochi
      </button>

      <div className="mb-5">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#ff5a5f]">
          <Sparkles className="h-3.5 w-3.5" /> Panel ekspresi
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Sistem ekspresi Mochi</h1>
        <p className="mt-1 text-[14px] text-[#8a8278]">Uji setiap mood, interaksi, dan gerak kepala ±15° sesuai rig-spec.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <div className="relative overflow-hidden rounded-3xl border border-[#e6d9cc]"
          style={{ background: "radial-gradient(ellipse at 36% 30%,#fbf6ed 0%,#eee4d5 62%,#e0d2c0 100%)" }}>
          <div className="h-[440px] w-full sm:h-[520px]">
            <Suspense fallback={<div className="grid h-full place-items-center text-[#a89f98]">Memuat Mochi…</div>}>
              <MochiPet3D ref={ref} initialMood="happy" accessory={acc} />
            </Suspense>
          </div>
          <div className="pointer-events-none absolute bottom-4 left-4 z-10 flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-[11px] font-medium text-[#8c7e6b] backdrop-blur">
            <Move3d className="h-3.5 w-3.5" /> Geser / arahkan kursor untuk lihat kepala miring
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <h2 className="mb-3 text-[16px] font-bold">Mood</h2>
            <div className="grid grid-cols-3 gap-2">
              {MOODS.map((m) => (
                <button key={m.id} onClick={() => setMood(m.id)} data-testid={`demo-mood-${m.id}`}
                  className={`rounded-2xl border p-2.5 text-center transition-colors ${active === m.id ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#eee5da] bg-[#fbf8f4]"}`}>
                  <div className="text-[13px] font-bold text-[#1e1b18]">{m.label}</div>
                  <div className="text-[11px] text-[#a19183]">{m.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <h2 className="mb-3 text-[16px] font-bold">Interaksi</h2>
            <div className="grid grid-cols-3 gap-2.5">
              {ACTIONS.map((a) => (
                <button key={a.id} onClick={() => doAction(a.id)} data-testid={`demo-action-${a.id}`}
                  className="flex flex-col items-center gap-1 rounded-2xl border border-[#eee5da] bg-[#f9f5f0] p-3 text-[#6e594c] transition-transform active:scale-95">
                  <a.icon className="h-5 w-5" />
                  <span className="text-[12px] font-bold">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <h2 className="mb-3 text-[16px] font-bold">Aksesori</h2>
            <div className="flex flex-wrap gap-2">
              {ALL_ACC.map((a) => (
                <button key={a.id} onClick={() => setAcc(a.id)} data-testid={`demo-acc-${a.id}`}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${acc === a.id ? "border-[#ff5a5f] bg-[#ffebeb] text-[#ff5a5f]" : "border-[#eee5da] bg-[#fbf8f4] text-[#6e594c]"}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-[16px] font-bold">Arah pandang</h2>
              <span className="text-[12px] font-semibold text-[#8a8278]">{look}°</span>
            </div>
            <input type="range" min="-15" max="15" value={look} onChange={(e) => onLook(Number(e.target.value))}
              data-testid="demo-look" className="w-full accent-[#ff5a5f]" />
            <p className="mt-2 text-[11px] text-[#a69995]">Dibatasi ±15° sesuai aturan rig (tidak ada rotasi 360°).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
